import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEventLogClinics from '../../hooks/useEventLogClinics';
import { EventLogClinic } from '../../services/eventLog.service';
import CheckboxDropdown from '../common/CheckboxDropdown';
import Header from '../layout/Header';

const PLAN_OPTIONS = [
  { label: 'EHR', value: 'ehr' },
  { label: 'Essentials', value: 'essentials' },
  { label: 'Trial', value: 'trial' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Canceled', value: 'canceled' },
  { label: 'Expired', value: 'expired' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Terminated', value: 'terminated' },
];

/**
 * Highlights matching text in a string. Returns JSX with <mark> tags.
 */
const HighlightText: React.FC<{ text: string; search: string }> = ({ text, search }) => {
  if (!search || !text) return <>{text}</>;

  const idx = text.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 rounded-sm px-0.5">
        {text.slice(idx, idx + search.length)}
      </mark>
      {text.slice(idx + search.length)}
    </>
  );
};

const EventLogClinics: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const { data, isFetching, fetchNextPage, isFetchingNextPage, hasNextPage } = useEventLogClinics();

  const allClinics = useMemo(() => {
    let clinics = data?.pages?.flatMap(page => page?.data || []) || [];

    if (planFilter.length) {
      clinics = clinics.filter(c => planFilter.includes(c.plan?.toLowerCase()));
    }

    if (statusFilter.length) {
      clinics = clinics.filter(c => statusFilter.includes(c.status?.toLowerCase()));
    }

    return clinics;
  }, [data, planFilter, statusFilter]);

  const columns = useMemo<ColumnDef<EventLogClinic, unknown>[]>(
    () => [
      {
        accessorFn: row => row.clinicId,
        header: 'ID',
        cell: ({ row }) => <HighlightText text={String(row.original.clinicId)} search={search} />,
        enableSorting: true,
      },
      {
        accessorKey: 'clinicName',
        header: 'Clinic',
        cell: ({ row }) => (
          <HighlightText
            text={row.original.clinicName || `Clinic ${row.original.clinicId}`}
            search={search}
          />
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'plan',
        header: 'Plan',
        cell: ({ row }) => <HighlightText text={row.original.plan || ''} search={search} />,
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <HighlightText text={row.original.status || ''} search={search} />,
        enableSorting: true,
      },
    ],
    [search]
  );

  return (
    <>
      <Header title="Event Logs" />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={allClinics}
          size="default"
          onRowClick={row => navigate(`/event-logs/${row.clinicId}`)}
          striped
          loading={isFetching && !allClinics.length}
          emptyState={<p className="text-gray-500">No clinics with event data found</p>}
          toolbar={{
            search: {
              placeholder: 'Search...',
              value: search,
              onChange: setSearch,
            },
            rightSlot: (
              <div className="flex items-center gap-2">
                <CheckboxDropdown
                  title="Plan"
                  options={PLAN_OPTIONS}
                  selected={planFilter}
                  onChange={setPlanFilter}
                />
                <CheckboxDropdown
                  title="Status"
                  options={STATUS_OPTIONS}
                  selected={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>
            ),
          }}
        />
        {hasNextPage && (
          <div className="mt-4 text-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {isFetchingNextPage ? 'Loading...' : 'Load more clinics'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default EventLogClinics;
